import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import Breadcrumbs from "../../common/Breadcrumbs";
import ServiceInvoiceScreen from "./serviceInvoice/ServiceInvoiceScreen";
import ExciseInvoiceScreen from "./exciseInvoice/ExciseInvoiceScreen";
import EditIcon from "../../assets/EditInvoice-icon.svg";
import EditInvoiceModal from "./invoiceDetails/InvoiceEditDetails";
import { useDispatch, useSelector } from "react-redux";
import { setFormData } from "../../redux/features/slices/formDataSlice";
import * as styles from "./InvoiceScreenStyles";
import Toast from "../../common/Toast";
import { RootState } from "../../redux/store";
import downloadIcon from "../../assets/download-icon.png";
import { invoiceEndPoints } from "../../constants/endpoints";
import { api } from "../../api/api";

import {
  getInvoiceDetails,
  InvoiceDataType,
  updateInvoice,
} from "../../api/invoice";
import { useNavigate } from "react-router-dom";

const InvoiceScreen = () => {
  const orderId = location.pathname.split("/").pop();
  const dispatch = useDispatch();
  const formData = useSelector((state: RootState) => state.formData);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const orgId = localStorage.getItem("orgId");
  const navigate = useNavigate();

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleConfirm = async (data: {
    exciseID: string;
    customerExecLeafNo: string;
    transporter: string;
    transporterPermitNo: string;
    vehicleNumber: string;
    serviceID?: string | null;
  }) => {
    // Update Redux
    dispatch(setFormData(data));

    // Update local state for UI consistency
    setValues((prev) => ({
      ...prev!,
      excise: {
        ...(prev?.excise || {}),
        exciseID: data.exciseID,
      },
      other: {
        ...(prev?.other || {}),
        customerExecLeafNo: data.customerExecLeafNo,
        transporter: data.transporter,
        transporterPermitNo: data.transporterPermitNo,
        vehicleNumber: data.vehicleNumber,
      },
    }));

    setOpenModal(false);
  };

  const handleUpdateInvoice = async () => {
    // Check if all fields are populated
    if (
      formData.exciseID &&
      formData.customerExecLeafNo &&
      formData.transporter &&
      formData.transporterPermitNo &&
      formData.vehicleNumber
    ) {
      try {
        const response = await updateInvoice(
          orderId!,
          formData.customerExecLeafNo ?? "",
          formData.transporterPermitNo ?? "",
          formData.transporter ?? "",
          formData.vehicleNumber ?? "",
          formData.exciseID ?? "",
          formData.serviceID
        );
        if (response.status === 201) {
          setToastMessage("Invoice details have been successfully updated.");
          setError(false);
          setTimeout(() => {
            navigate(`/${orgId}/seller/order-summary/${orderId}`);
          }, 1000);
        }
      } catch (err: any) {
        console.error(err);
        setToastMessage(err.message ?? "Failed to update the invoice.");
        setError(true);
      }
    } else {
      setToastMessage(
        "Failed to update the invoice. Please ensure all fields are correct and try again."
      );
      setError(true);
    }
    setShowToast(true);
  };
  const downloadInvoice = async () => {
    try {
      const response = await api.get(
        `${invoiceEndPoints.downloadInvoice}/${orderId}`
      );
      if (response.data.data) {
        const link = document.createElement("a");
        link.href = response.data.data;
        link.download = `invoice-${orderId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };
  useEffect(() => {
    const getValues = async () => {
      try {
        const response = await getInvoiceDetails(orderId!);
        if (response?.data.data) setValues(response.data.data);
      } catch (err) {
        console.error("Failed to fetch ", err);
      }
    };
    getValues();
  }, []);

  const [values, setValues] = useState<InvoiceDataType>();

  return (
    <>
      <Box sx={styles.mainLayoutBox}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          paths={["All Orders", "Edit Order Status", "Create Invoice"]}
          links={[
            "/seller/orders",
            `/seller/order-summary/${orderId}`,
            "invoice-screen",
          ]}
        />

        {/* Main Layout */}
        <Box sx={styles.invoiceLayoutBox}>
          {/* Excise Invoice */}
          <Box sx={styles.invoiceBox}>
            <Typography variant="h6" sx={styles.titleTypography}>
              Excise Invoice
            </Typography>
            <Box sx={styles.borderBox}>
              <ExciseInvoiceScreen data={values} />
            </Box>
          </Box>

          {/* Service Invoice */}
          <Box sx={styles.invoiceBox}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={styles.titleTypography}>
                Service Invoice
              </Typography>
              <Box sx={styles.downloadButton}>
                <img
                  style={{
                    width: "28px",
                    height: "28px",
                    cursor: "pointer",
                  }}
                  onClick={downloadInvoice}
                  src={downloadIcon}
                  alt="download-icon"
                />
                <img
                  src={EditIcon}
                  style={{ width: "28px", height: "28px", cursor: "pointer" }}
                  onClick={handleOpenModal}
                  alt="Edit Icon"
                />
              </Box>
            </Box>
            <Box sx={styles.borderBox}>
              <ServiceInvoiceScreen data={values} />
            </Box>
            <Box sx={styles.updateButtonBox}>
              <Button
                variant="contained"
                sx={styles.updateButton}
                onClick={handleUpdateInvoice}
              >
                Update Invoice
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Modal Integration */}
      <EditInvoiceModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        initialFormData={{
          exciseID: (formData?.exciseID || values?.excise?.exciseID) ?? "",
          serviceID: formData?.serviceID ?? values?.service?.serviceID,
          customerExecLeafNo:
            (formData?.customerExecLeafNo ||
              values?.other?.customerExecLeafNo) ??
            "",
          transporter:
            (formData?.transporter || values?.other?.transporter) ?? "",
          transporterPermitNo:
            (formData?.transporterPermitNo ||
              values?.other?.transporterPermitNo) ??
            "",
          vehicleNumber:
            (formData?.vehicleNumber || values?.other?.vehicleNumber) ?? "",
        }}
      />

      {/* Toast Component */}
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default InvoiceScreen;
